import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ContactModal from '../../../components/feature/ContactModal';
import SEO from '../../../components/feature/SEO';

// Get Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function Partners() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-affiliate-working`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          type: 1 // Partners
        }),
      });

      const result = await response.json();
      console.log('Email response:', result);

      if (result.success) {
        setSubmitMessage('✅ Application submitted successfully! We will contact you within 48 hours.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          message: ''
        });
      } else {
        setSubmitMessage(`❌ Failed to submit application: ${result.error}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitMessage('❌ Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="QBOVerifier Partner Program – Recurring Revenue from Invoice Security Referrals"
        description="Become a QBOVerifier reseller partner. Earn up to 30% recurring commission selling QuickBooks invoice fraud protection. Bronze, Silver, and Gold partner tiers available."
        keywords="QBOVerifier partner program, QuickBooks reseller program, invoice security revenue, recurring commission program, QuickBooks affiliate partner"
        canonical="/affiliates/partners"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'QBOVerifier Reseller Partner Program',
          description: 'Reseller partner program with tiered commissions for selling QuickBooks invoice fraud protection.',
          url: `${import.meta.env.VITE_SITE_URL || ''}/affiliates/partners`,
          publisher: {
            '@type': 'Organization',
            name: 'QBOVerifier',
          },
        }}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-red-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Unlock Recurring Revenue Opportunities
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Join the QBOVerifier Partner Program and tap into the growing demand for invoice security 
                solutions. Generate substantial recurring revenue while helping businesses protect their reputation.
              </p>
              <button 
                onClick={() => setIsContactModalOpen(true)}
                className="bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-orange-700 transition-colors shadow-lg whitespace-nowrap"
              >
                <i className="ri-handshake-line mr-2"></i>
                Become a Partner Today
              </button>
            </div>
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/60cfa5760427af1c3d73f4665d1a36c9/c79a0b769c871b5fb7efe1516bbfd5ae.jpeg"
                alt="Successful business partnership"
                className="rounded-xl shadow-2xl w-full object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Opportunity */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Massive Market Opportunity
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Invoice fraud costs businesses billions annually. Position yourself in this rapidly growing security market.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">$50B+</div>
              <p className="text-gray-700 font-medium">Annual losses from invoice fraud globally</p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">2.5M+</div>
              <p className="text-gray-700 font-medium">QuickBooks Online users worldwide</p>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-8 rounded-xl text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">35%</div>
              <p className="text-gray-700 font-medium">Annual growth in cybersecurity spending</p>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Partner Program Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to build a successful QBOVerifier reseller business.
            </p>
          </div>
          
          <div className="space-y-20">
            {/* Benefit 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Generous Commission Structure
                </h3>
                <p className="text-xl text-gray-600 mb-6">
                  Earn substantial recurring commissions on every client you bring to QBOVerifier. 
                  Our tiered structure rewards high-performing partners with even higher rates.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-money-dollar-circle-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Up to 30% recurring commission on all sales</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-line-chart-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Performance bonuses for top partners</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-calendar-check-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Monthly commission payments</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Business%20partner%20celebrating%20financial%20success%20with%20commission%20earnings%20charts%20and%20revenue%20growth%20graphs%2C%20successful%20reseller%20program%20results%2C%20profitable%20partnership%20visualization%2C%20modern%20office%20celebration&width=600&height=400&seq=commission-structure&orientation=landscape"
                  alt="Commission earnings"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20sales%20training%20session%20with%20marketing%20materials%20and%20support%20resources%2C%20partner%20enablement%20program%2C%20comprehensive%20business%20training%2C%20sales%20success%20tools%20and%20resources&width=600&height=400&seq=sales-support&orientation=landscape"
                  alt="Sales and marketing support"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Complete Sales & Marketing Support
                </h3>
                <p className="text-xl text-gray-600 mb-6">
                  We provide strong tutorial support for you to on-explain the benefits and usability of QBOVerifier.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-presentation-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Professional delivery support and demos</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-customer-service-2-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Partner success manager</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Easy-to-Sell Solution
                </h3>
                <p className="text-xl text-gray-600 mb-6">
                  QBOVerifier practically sells itself. The value proposition is clear, the need is urgent, 
                  and the ROI is immediate. Perfect for building a successful reseller business.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3">
                    <i className="ri-shield-check-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Clear value proposition that resonates with businesses</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-time-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Quick implementation and immediate results</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="ri-repeat-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">High customer retention and satisfaction rates</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Business%20professional%20easily%20presenting%20QBOVerifier%20solution%20to%20interested%20clients%2C%20successful%20sales%20meeting%20with%20positive%20client%20reactions%2C%20easy%20selling%20process%20demonstration%2C%20confident%20sales%20presentation&width=600&height=400&seq=easy-selling&orientation=landscape"
                  alt="Easy selling process"
                  className="rounded-xl shadow-2xl w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Tiers */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Partner Tier Structure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grow your earnings as you build your QBOVerifier business with our progressive tier system.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-medal-line text-gray-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Bronze Partner</h3>
                <p className="text-gray-600">0-25 active clients</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">20% recurring commission</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Basic marketing materials</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Email support</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Monthly training webinars</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-orange-300 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">Better Value</span>
              </div>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-orange-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Silver Partner</h3>
                <p className="text-gray-600">26-100 active clients</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">25% recurring commission</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Premium marketing materials</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Priority phone support</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Quarterly business reviews</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Co-marketing opportunities</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-yellow-300">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vip-crown-line text-yellow-600 text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Gold Partner</h3>
                <p className="text-gray-600">100+ active clients</p>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">30% recurring commission</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Custom marketing materials</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Dedicated success manager</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Monthly strategy sessions</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Exclusive partner events</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-green-500"></i>
                  <span className="text-gray-700">Performance bonuses</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Partner Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our partners are building successful businesses with QBOVerifier.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20person%20headshot%20portrait%2C%20successful%20partner%20testimonial%20photo%2C%20confident%20business%20leader%2C%20modern%20professional%20appearance&width=80&height=80&seq=partner2&orientation=squarish"
                  alt="Partner testimonial"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">Marianne Rodriguez</h4>
                  <p className="text-gray-600">SecureFlow Accounting Solutions</p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                "The support from QBOVerifier has been exceptional. They've helped us build a thriving 
                reseller business that's exceeded all our expectations."
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  97+ clients
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Start Building Your QBOVerifier Business
              </h2>
              <p className="text-xl mb-8">
                Join our partner program today and tap into the massive opportunity in invoice security solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg whitespace-nowrap"
                >
                  <i className="ri-rocket-line mr-2"></i>
                  Apply to Become a Partner
                </button>
              </div>
            </div>
            
            {/* Application Form */}
            <div className="bg-white p-8 rounded-xl shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Apply Now</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name *"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address *"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Company Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900"
                  />
                </div>
                
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your business and why you want to become a partner..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm text-gray-900 resize-vertical"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Submit Application
                    </>
                  )}
                </button>
                
                {submitMessage && (
                  <div className={`p-4 rounded-lg ${
                    submitMessage.includes('error') 
                      ? 'bg-red-50 border border-red-200 text-red-700' 
                      : 'bg-green-50 border border-green-200 text-green-700'
                  }`}>
                    <div className="flex items-center">
                      <i className={`${
                        submitMessage.includes('error') 
                          ? 'ri-error-warning-line text-red-500' 
                          : 'ri-check-circle-line text-green-500'
                      } text-xl mr-3`}></i>
                      <p>{submitMessage}</p>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title="Become a QBOVerifier Partner"
        subtitle="Let's discuss your partnership opportunity"
      />
    </div>
  );
}
